/**
 * semantic-tags.ts — Fonte única de verdade.
 * Nenhum outro arquivo define tags, categorias, cores ou tooltips.
 */

// ═══════════════════════════════════════
// SEÇÃO 1 — Tags (15 tags Feynman)
// ═══════════════════════════════════════

export const BASE_TAGS: readonly string[] = [
  'problema', 'conceito', 'contexto', 'consenso',
  'tese', 'justificativa', 'divergência',
  'questionamento', 'lacuna', 'limites',
  'método', 'resultado', 'evidência',
  'contribuição', 'posição',
] as const;

export function getEffectiveValidTags(userCustomTags: string[] = []): string[] {
  return [...BASE_TAGS, ...userCustomTags];
}

export const VALID_TAGS: string[] = [...BASE_TAGS];

// (C29) Label centralizado para highlights sem tag
export const UNTAGGED_LABEL = 'SEM TAG';

// ═══════════════════════════════════════
// SEÇÃO 2 — Categorias (novo mapeamento Feynman)
// ═══════════════════════════════════════

export const SATURATION_CATEGORIES: Record<string, string[]> = {
  'EXPLICATIVO':    ['problema', 'conceito', 'contexto', 'consenso'],
  'ARGUMENTATIVO':  ['tese', 'justificativa', 'divergência'],
  'REFLEXIVO':      ['questionamento', 'lacuna', 'limites'],
  'EMPÍRICO':       ['método', 'resultado', 'evidência'],
  'CRÍTICO':        ['contribuição', 'posição'],
};

// Mesmos hex vibrantes — NÃO MUDAM
export const SATURATION_COLORS: Record<string, string> = {
  'EXPLICATIVO': '#3b82f6',
  'ARGUMENTATIVO': '#ef4444',
  'REFLEXIVO': '#8b5cf6',
  'EMPÍRICO': '#10b981',
  'CRÍTICO': '#f59e0b',
};

// Ordem canônica. Inclui 'SEM TAG' via UNTAGGED_LABEL.
export const CATEGORY_ORDER: string[] = [
  'EXPLICATIVO', 'ARGUMENTATIVO', 'REFLEXIVO', 'CRÍTICO', 'EMPÍRICO', UNTAGGED_LABEL,
];

export const CATEGORY_EMOJIS: Record<string, string> = {
  'EXPLICATIVO': '🔵',
  'ARGUMENTATIVO': '🔴',
  'REFLEXIVO': '🟣',
  'CRÍTICO': '🟠',
  'EMPÍRICO': '🟢',
  [UNTAGGED_LABEL]: '🟡',
};

// Para TagsOmniFocus UI (pills)
export const CONTENT_TAG_GROUPS: Record<string, string[]> = {
  'EXPLICATIVO':    ['problema', 'conceito', 'contexto', 'consenso'],
  'ARGUMENTATIVO':  ['tese', 'justificativa', 'divergência'],
  'REFLEXIVO':      ['questionamento', 'lacuna', 'limites'],
  'EMPÍRICO':       ['método', 'resultado', 'evidência'],
  'CRÍTICO':        ['contribuição', 'posição'],
};

// usageTags — exportar vazio (removido da UI, dados preservados no Drive)
export const USAGE_TAG_GROUPS: Record<string, string[]> = {};

// ═══════════════════════════════════════
// SEÇÃO 3 — Cores de highlight (3 formatos)
// ═══════════════════════════════════════

// Default sem tag: MANTIDO #FFEB3B (decisão Adrian 24/mar)
export const DEFAULT_HIGHLIGHT_COLOR = '#FFEB3B';

// Categoria → cor pastel de highlight (usada APENAS para tags atribuídas no PDF)
const CATEGORY_HIGHLIGHT_COLORS: Record<string, string> = {
  'EXPLICATIVO': '#A8C8E8',
  'ARGUMENTATIVO': '#E8B8B8',
  'REFLEXIVO': '#C8B8D8',
  'EMPÍRICO': '#A8D8C8',
  'CRÍTICO': '#E8D8A8',
};

// ── FORMATO A: Record<string, string> (PdfReaderModal/constants.ts) ──
// tag → cor de highlight no PDF
export const TAG_HIGHLIGHT_COLORS: Record<string, string> = Object.fromEntries(
  Object.entries(SATURATION_CATEGORIES).flatMap(([cat, tags]) =>
    tags.map(tag => [tag, CATEGORY_HIGHLIGHT_COLORS[cat]])
  )
);

// ── FORMATO B: { label, color, subtags[] }[] (HighlightsSummaryView.tsx) ──
// color = SATURATION_COLORS (vibrante). Para barras/labels de categoria na UI.
export const CONTENT_TAGS_WITH_SUBTAGS = CATEGORY_ORDER
  .filter(cat => cat !== UNTAGGED_LABEL)
  .map(cat => ({
    label: cat,
    color: SATURATION_COLORS[cat],
    subtags: SATURATION_CATEGORIES[cat],
  }));

// TAG_COLORS derivado — tag → cor vibrante da categoria (para UI: barras, badges, pills)
// (C40) NÃO confundir com TAG_HIGHLIGHT_COLORS (pastéis, para PDF).
//   TAG_COLORS = vibrante → para UI
//   TAG_HIGHLIGHT_COLORS = pastel → para PDF
export const TAG_COLORS: Record<string, string> = Object.fromEntries(
  Object.entries(SATURATION_CATEGORIES).flatMap(([cat, tags]) =>
    tags.map(tag => [tag, SATURATION_COLORS[cat]])
  )
);

// ── FORMATO C: Record<string, { label, color }> (ExportAnnotationsModal.tsx) ──
// color = SATURATION_COLORS (vibrante). Consistente com Formato B.
export const TAG_CATEGORY_MAP: Record<string, { label: string; color: string }> =
  Object.fromEntries(
    Object.entries(SATURATION_CATEGORIES).flatMap(([cat, tags]) =>
      tags.map(tag => [tag, { label: cat, color: SATURATION_COLORS[cat] }])
    )
  );

// ═══════════════════════════════════════
// SEÇÃO 4 — Tooltips e descrições (15 tags Feynman)
// ═══════════════════════════════════════

export const TAG_TOOLTIPS: Record<string, string> = {
  'problema': 'Qual é o problema? Questão de pesquisa',
  'conceito': 'O que isso significa? Definição de ideia-chave',
  'contexto': 'Qual é o cenário? Panorama, história, situação',
  'consenso': 'O que o campo aceita? Ponto estabelecido',
  'tese': 'O que o autor defende? Posição central',
  'justificativa': 'Por que defende? Raciocínio de sustentação',
  'divergência': 'Onde discorda? Confronto com outra posição',
  'questionamento': 'O que pergunta? Dúvida, problematização',
  'lacuna': 'O que falta? Ausência identificada no campo',
  'limites': 'Até onde alcança? Restrição, limitação',
  'método': 'Como investigou? Caminho de pesquisa',
  'resultado': 'O que encontrou? Achados da investigação',
  'evidência': 'O que comprova? Dado concreto de apoio',
  'contribuição': 'O que é novo aqui? O que acrescenta ao campo',
  'posição': 'De onde fala? Lugar do autor no debate',
};

export const TAG_DESCRIPTIONS: Array<{ tag: string; description: string }> = [
  { tag: 'problema', description: 'O trecho apresenta a questão que motivou a pesquisa' },
  { tag: 'conceito', description: 'O trecho define ou explica uma ideia-chave' },
  { tag: 'contexto', description: 'O trecho dá panorama, história ou estado da arte' },
  { tag: 'consenso', description: 'O trecho mostra algo aceito, não disputado no campo' },
  { tag: 'tese', description: 'O trecho afirma a posição central do autor' },
  { tag: 'justificativa', description: 'O trecho apresenta razões, fundamentos ou raciocínio que sustentam uma posição' },
  { tag: 'divergência', description: 'O trecho contesta ou confronta outra posição' },
  { tag: 'questionamento', description: 'O trecho levanta dúvida ou problematiza' },
  { tag: 'lacuna', description: 'O trecho aponta algo que não foi pesquisado' },
  { tag: 'limites', description: 'O trecho reconhece restrição ou limitação' },
  { tag: 'método', description: 'O trecho descreve o caminho de pesquisa' },
  { tag: 'resultado', description: 'O trecho apresenta o que a investigação revelou' },
  { tag: 'evidência', description: 'O trecho traz dado concreto que sustenta algo' },
  { tag: 'contribuição', description: 'O trecho mostra o que este trabalho acrescenta ao campo' },
  { tag: 'posição', description: 'O trecho revela em qual corrente ou tradição o autor se situa' },
];

// ═══════════════════════════════════════
// SEÇÃO 5 — Funções
// ═══════════════════════════════════════

/** Normaliza um tag para o nome atual (aplica migrações). */
export function normalizeTag(tag: string): string {
  const lower = tag.toLowerCase();
  return TAG_MIGRATION_MAP[lower] || lower;
}

/** Normaliza array de tags, removendo duplicatas pós-migração. */
export function normalizeContentTags(tags: string[]): string[] {
  if (!tags || tags.length === 0) return tags;
  const seen = new Set<string>();
  const result: string[] = [];
  for (const tag of tags) {
    const normalized = normalizeTag(tag);
    if (!seen.has(normalized)) {
      seen.add(normalized);
      result.push(normalized);
    }
  }
  return result;
}

export function resolvePdfColor(tags: string[]): string {
  if (!tags || tags.length === 0) return DEFAULT_HIGHLIGHT_COLOR;
  for (const tag of tags) {
    const normalized = normalizeTag(tag);
    const color = TAG_HIGHLIGHT_COLORS[normalized];
    if (color) return color;
  }
  return DEFAULT_HIGHLIGHT_COLOR;
}

export function isValidTag(tag: string, customTags: string[] = []): boolean {
  const allTags = getEffectiveValidTags(customTags);
  return allTags.includes(tag.toLowerCase());
}

export function removeAccents(str: string): string {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

export const TAG_LOOKUP: Map<string, string> = new Map(
  VALID_TAGS.map(tag => [removeAccents(tag.toLowerCase()), tag])
);

// ═══════════════════════════════════════
// SEÇÃO 6 — Prompts
// ═══════════════════════════════════════

/**
 * Gera prompt para classify-tags (batch).
 * ATENÇÃO: Formato de resposta usa campo "n" (1-based), NÃO "id".
 */
export function buildClassifyPrompt(
  highlights: Array<{ index: number; text: string }>
): string {
  const tagList = VALID_TAGS.map(tag => {
    const tooltip = TAG_TOOLTIPS[tag];
    return tooltip ? `- ${tag} \u2192 ${tooltip}` : `- ${tag}`;
  }).join('\n');

  const highlightsJson = highlights
    .map((h, i) => `[${i + 1}] ${h.text}`)
    .join('\n');

  return `Você é um assistente de leitura acadêmica. Classifique os trechos abaixo.

Para cada trecho, atribua 1 a 3 tags da lista. Escolha apenas as que se aplicam claramente.

TAGS DISPONÍVEIS:
${tagList}

REGRAS:
- Use APENAS tags desta lista.
- Prefira 1 tag quando o trecho tem função clara e única.
- Use 2 tags quando o trecho genuinamente cumpre duas funções distintas.
- Use 3 tags raramente \u2014 apenas quando as três são igualmente relevantes.
- Se não se encaixa claramente, retorne array vazio.

NOTAS DE DESAMBIGUAÇÃO:
- resultado = o que a pesquisa encontrou (dados, achados concretos)
- contribuição = o que esses achados SIGNIFICAM para o campo (avanço, inovação)
- posição = use apenas quando o autor EXPLICITAMENTE se situa numa escola, tradição ou corrente

Responda APENAS com JSON, sem texto adicional.

TRECHOS:
${highlightsJson}

FORMATO (JSON puro):
[{"n": 1, "tags": ["tag1", "tag2"]}, {"n": 2, "tags": ["tag1"]}]`;
}

/**
 * Gera prompt para suggest-tags (single).
 * SEPARADO de buildClassifyPrompt — formatos incompatíveis.
 */
export function buildSuggestPrompt(text: string): string {
  const tagList = VALID_TAGS.join(', ');
  return `Você é um assistente de leitura acadêmica. Sugira UMA ou DUAS tags para o trecho.

Tags: ${tagList}.

Trecho: "${text}"

Responda APENAS com tags separadas por vírgula. Exemplo: conceito, contexto`;
}

// ═══════════════════════════════════════
// SEÇÃO 7 — Migração de tags (14 antigas → 15 novas)
// ═══════════════════════════════════════

export const TAG_MIGRATION_MAP: Record<string, string> = {
  'definição': 'conceito',
  'distinção': 'conceito',
  'tipologia': 'conceito',
  'argumento': 'justificativa',    // renomeado 31/mar — epistemologicamente mais preciso
  'evidência': 'evidência',
  'exemplo': 'evidência',
  'articulação teórica': 'justificativa',
  'pressuposto': 'justificativa', // decisão Adrian 25/mar (spec tinha 'posição')
  'implicação': 'resultado',      // decisão Adrian 25/mar (spec tinha 'contribuição')
  'problematização': 'questionamento',
  'crítica': 'divergência',
  'delimitação': 'limites',
  'qualitativo': 'método',
  'quantitativo': 'método',
};

/**
 * Migra tags de v1 → v2.
 * Chamado por schemaMigration.ts no case 1 / case undefined.
 *
 * (C35) DEDUPLICAÇÃO OBRIGATÓRIA: múltiplas tags antigas podem mapear para
 * o mesmo destino (ex: definição, distinção, tipologia → todas viram conceito).
 * Sem [...new Set(migrated)], dados ficam corrompidos com duplicatas.
 */
export function migrateTagsV1toV2(contentTags: string[]): {
  tags: string[];
  color: string;
  changed: boolean;
} {
  const migrated = contentTags.map(tag => TAG_MIGRATION_MAP[tag] || tag);
  const deduplicated = [...new Set(migrated)];
  const changed = deduplicated.length !== contentTags.length ||
                  deduplicated.some((t, i) => t !== contentTags[i]);
  return {
    tags: deduplicated,
    color: resolvePdfColor(deduplicated),
    changed,
  };
}

// ═══════════════════════════════════════
// SEÇÃO 8 — Perguntas-guia
// ═══════════════════════════════════════

export const PERGUNTAS_GUIA: Record<string, string[]> = {
  'problema': [
    'Como este problema se relaciona com a questão central do seu trabalho?',
    'Este problema é abordado por outros autores de maneira distinta?',
    'Que aspecto deste problema seu trabalho busca avançar?',
  ],
  'conceito': [
    'Como este conceito se encaixa no quadro teórico do seu trabalho?',
    'Outros autores definem este conceito de maneira diferente?',
    'Este conceito é central ou auxiliar no seu argumento?',
  ],
  'contexto': [
    'Este contexto ajuda a situar qual aspecto do seu trabalho?',
    'Há mudanças neste cenário que afetam sua análise?',
    'Como este panorama se compara ao contexto de outros estudos?',
  ],
  'consenso': [
    'Você adota este consenso ou pretende questioná-lo?',
    'Este ponto pacífico serve de fundamento para qual parte do seu argumento?',
    'Há autores que discordam deste consenso?',
  ],
  'tese': [
    'A tese deste autor reforça ou desafia a sua?',
    'Em que medida esta posição se alinha com seu quadro teórico?',
    'Que evidências o autor apresenta para sustentar esta tese?',
  ],
  'justificativa': [
    'Esta justificativa sustenta ou enfraquece sua posição?',
    'Que premissas fundamentam este raciocínio?',
    'Existe contra-argumento na literatura que você deve considerar?',
  ],
  'divergência': [
    'Que aspecto da divergência é mais relevante para seu trabalho?',
    'Como você se posiciona diante desta discordância?',
    'Esta divergência revela uma lacuna que seu trabalho pode preencher?',
  ],
  'questionamento': [
    'Esta dúvida é resolvida por outros autores ou permanece aberta?',
    'Este questionamento se conecta com a sua pergunta de pesquisa?',
    'Sua pesquisa oferece elementos para responder a esta questão?',
  ],
  'lacuna': [
    'Seu trabalho pretende preencher esta lacuna?',
    'Esta ausência é reconhecida por outros autores?',
    'Que tipo de pesquisa seria necessário para preencher esta lacuna?',
  ],
  'limites': [
    'Estes limites também se aplicam ao seu trabalho?',
    'Como você pode mitigar estas limitações na sua pesquisa?',
    'Outros autores encontraram formas de superar estas restrições?',
  ],
  'método': [
    'Este método é compatível com a abordagem do seu trabalho?',
    'Que adaptações seriam necessárias para seu contexto?',
    'Outros autores utilizam métodos diferentes para questões similares?',
  ],
  'resultado': [
    'Estes achados confirmam ou contradizem sua hipótese?',
    'Os resultados são generalizáveis para o seu contexto?',
    'Como estes achados se comparam com os de outros estudos?',
  ],
  'evidência': [
    'Esta evidência é suficiente para sustentar a afirmação?',
    'Existem evidências contrárias na literatura?',
    'Como este dado se relaciona com as evidências do seu trabalho?',
  ],
  'contribuição': [
    'Esta contribuição avança o campo em que direção?',
    'Seu trabalho dialoga com esta contribuição ou a complementa?',
    'Que novas questões esta contribuição abre?',
  ],
  'posição': [
    'Você se situa na mesma tradição ou em tradição diferente?',
    'Esta posição epistemológica é compatível com seu referencial?',
    'Como esta posição influencia as conclusões do autor?',
  ],
};

export const PERGUNTAS_GUIA_FALLBACK: string[] = [
  'Como este trecho se conecta com o argumento central do seu trabalho?',
  'Outros autores abordam esta questão de maneira diferente?',
  'Que aspecto deste trecho é mais relevante para sua pesquisa?',
];

export function getPerguntasGuia(tag: string): string[] {
  return PERGUNTAS_GUIA[tag.toLowerCase()] || PERGUNTAS_GUIA_FALLBACK;
}
